import { Card, CardContent } from "@/components/ui/card";
import { Receipt, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/lib/currency";
import { useTranslation } from "react-i18next";

interface ReceiptItem {
  name: string;
  amount: number;
  category: "food" | "household" | "health" | "personal" | "other";
  quantity: number;
  price: number;
}

interface ReceiptData {
  id: string; // receipts.id is a UUID
  date: string;
  store: string;
  total: number;
  items: ReceiptItem[];
  imageUrl: string;
  address?: string;
  store_id?: string | null;
  currency?: string;
}

interface RecentReceiptsListProps {
  receipts: ReceiptData[];
  onReceiptClick: (receipt: ReceiptData) => void;
}

export const RecentReceiptsList = ({ receipts, onReceiptClick }: RecentReceiptsListProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const recentReceipts = receipts.slice(0, 3);

  const handleStoreClick = (e: React.MouseEvent, storeId: string | null | undefined) => {
    e.stopPropagation();
    if (storeId) {
      navigate(`/stores/${storeId}`);
    }
  };

  if (recentReceipts.length === 0) {
    return (
      <Card className="shadow-custom-md border-0">
        <CardContent className="p-6 text-center">
          <Receipt className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t('dashboard.noReceipts')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {recentReceipts.map((receipt) => (
        <Card
          key={receipt.id}
          className="shadow-custom-md border-0 cursor-pointer hover:shadow-custom-lg transition-all duration-300"
          onClick={() => onReceiptClick(receipt)}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                  <Receipt className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p 
                    className="font-semibold truncate text-primary hover:underline cursor-pointer"
                    onClick={(e) => handleStoreClick(e, receipt.store_id)}
                  >
                    {receipt.store}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(receipt.date), "MMM d, yyyy")}
                  </p>
                  <p className="text-sm font-medium text-primary mt-1">
                    {formatCurrency(receipt.total, receipt.currency)}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
